'use client';

import React, { useMemo } from 'react';
import styles from './ContactUsButtonPanel.module.scss';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibCallIcon, KibChatFillIcon, KibEmailFillIcon } from '@chewy/kib-icons-react';
import OHCContext from '@/providers/OHCContext';
import { isRXTopicSelected, navigateToLink } from '@/utils/helpCenterUtils';
import { useRouter } from 'next/navigation';
import { useTrack } from '@chewy/use-analytics';
import URLGenerator from '@/utils/URLGenerator';

interface ContactUsButtonPanelProps {
  /** Optional title text to display above the buttons */
  title?: string;
  /** Optional description text to display above the buttons */
  description?: string;
  /** Additional CSS class name */
  className?: string;
  /** Article ID for analytics tracking */
  articleId?: string;
  /** Article title for analytics tracking */
  articleTitle?: string;
  /** Context for analytics tracking (e.g., 'article-sentiment', 'help-center') */
  analyticsContext?: string;
  /** Whether to show the panel in a compact layout */
  compact?: boolean;
}

const ContactUsButtonPanel: React.FC<ContactUsButtonPanelProps> = ({
  title,
  description,
  className = '',
  articleId,
  articleTitle,
  analyticsContext = 'contact-us',
  compact = false
}) => {
  const router = useRouter();
  const { selectedTopic, isMobileApp, categoryIdsMap } = React.useContext(OHCContext);
  const track = useTrack();
  
  const isRXTopic = useMemo(() => isRXTopicSelected(selectedTopic?.categoryId, categoryIdsMap), [selectedTopic, categoryIdsMap]);
  const generalPhone = '1-800-672-4399';
  const rxPhone = '1-877-977-3879';
  // const email = 'help@chewy.com';
  const phoneNumber = useMemo(() => (isRXTopic ? rxPhone : generalPhone), [isRXTopic]);

  const handleChatClick = () => {
    track(`${isRXTopic ? 'RX ' : ''}Chat Click${articleTitle ? ` - ${articleTitle}` : ''}`, {
      id: `${analyticsContext}-id`,
      parentId: `${analyticsContext}-id`,
      level: analyticsContext,
      type: 'static',
      position: 1,
      event_name: 'Button Clicked',
      eventAction: 'click',
      eventCategory: 'help-center-contact',
      eventLabel: `help-center-contact-${isRXTopic ? 'rx-' : ''}chat-click`,
      articleId: articleId,
      articleTitle: articleTitle
    });

    let urlGenerator = new URLGenerator(window.location.origin + '/csrb/nma');
    if (!isMobileApp) {
      urlGenerator = new URLGenerator(window.location.origin + window.location.pathname);
      urlGenerator.addParameter({ name: 'signInFromChat', value: 'true' });
    }

    if (isRXTopic) {
      urlGenerator.addParameter({ name: 'pageContext', value: 'RX' });
    }

    console.log(`********* urlGenerator.generateURL(): ${urlGenerator.generateURL()}`);

    // TODO: This is a temporary fix to redirect to the chat page. We need to find a better way to do this.
    // we have to do this so that the Home/Nav will show the chat window

    window.location.href = urlGenerator.generateURL();
  };

  const handleCallClick = () => {
    track(`${isRXTopic ? 'RX ' : ''}Phone Click${articleTitle ? ` - ${articleTitle}` : ''}`, {
      id: `${analyticsContext}-id`,
      parentId: `${analyticsContext}-id`,
      level: analyticsContext,
      type: 'static',
      position: 1,
      event_name: 'Button Clicked',
      eventAction: 'click',
      eventCategory: 'help-center-contact',
      eventLabel: `help-center-contact-${isRXTopic ? 'rx-' : ''}phone-click`,
      articleId: articleId,
      articleTitle: articleTitle
    });

    // Handle external links (http or https)
    navigateToLink(`tel:${phoneNumber}`, router);
  };

  // const handleEmailClick = () => {
  //   track(`${isRXTopic ? 'RX ' : ''}Email Click${articleTitle ? ` - ${articleTitle}` : ''}`, {
  //     id: `${analyticsContext}-id`,
  //     parentId: `${analyticsContext}-id`,
  //     level: analyticsContext,
  //     type: 'static',
  //     position: 1,
  //     event_name: 'Button Clicked',
  //     eventAction: 'click',
  //     eventCategory: 'help-center-contact',
  //     eventLabel: `help-center-contact-${isRXTopic ? 'rx-' : ''}email-click`,
  //     articleId: articleId,
  //     articleTitle: articleTitle
  //   });
  //
  //   navigateToLink(`mailto:${email}`, router);
  // };

  React.useEffect(() => {
    track(`Contact Us Panel Viewed${articleTitle ? ` - ${articleTitle}` : ''}`, {
      id: `${analyticsContext}-id`,
      parentId: `${analyticsContext}-id`,
      level: analyticsContext,
      type: 'static',
      position: 1,
      event_name: 'Widget Viewed',
      eventAction: 'impression',
      eventCategory: 'help-center-contact',
      eventLabel: 'help-center-contact-view',
      articleId: articleId,
      articleTitle: articleTitle
    });
  }, []);

  return (
    <div data-testid="contact-us-button-panel" className={`${styles.contactUsContainer} ${compact ? styles.compact : ''} ${className}`}>
      {title && <div className={styles.title}>{title}</div>}
      {description && <div className={styles.description}>{description}</div>}
      <div className={styles.buttonContainer}>
        <KibButtonNew
          size="small"
          emphasis="tertiary"
          className={styles.button}
          onClick={handleChatClick}
          data-testid="chat-button"
          icon={<KibChatFillIcon />}
        >
          Chat now
        </KibButtonNew>
        {/* {isRXTopic && (
        <KibButtonNew
          size="small"
          emphasis="tertiary"
          onClick={handleEmailClick}
          data-testid="email-button"
          icon={<KibEmailFillIcon />}
        >
            Email
          </KibButtonNew>
        )} */}
        <KibButtonNew 
          size="small"
          emphasis="tertiary"
          className={styles.button} 
          onClick={handleCallClick}
          data-testid="phone-button"
          icon={<KibCallIcon />}
        >
          {phoneNumber}
        </KibButtonNew>
      </div>
    </div>
  );
};

export default ContactUsButtonPanel;
